<script setup>
import { ref, onMounted } from "vue";
import axios from "axios";
import i18n from "@/i18n";
import ChangeDevices from "@/components/Common/ChangeDevices.vue";
import { computed } from "vue";
import { useDeviceState } from "@/stores/deviceState";
import configurationProvider from "@/Core/configurationProvider";

import VueMarquee from "@/../../../Web.Admin/admin-frontend/modules/Announcement/components/VueMarquee.vue";
import { useRoute } from "vue-router";
import { ModuleManager } from "@module-system";

const route = useRoute();

var isDemoActive = configurationProvider.getBool("DEMO_ACTIVE", false);

const deviceState = useDeviceState();
const rtcId = ref("");
const counter = ref(0);
const name = ref("");
const error = ref("");
const dialed = ref(false);

const inQueue = ref(false);

const canGetRtcId = ref(false);

const marqueeDisabled = ref(true);
const marqueeAnnouncements = ref([]);

const canPlaceCall = ref(true);

function generateRtcId() {
  counter.value = 0;
  dialed.value = false;
  error.value = "";
  inQueue.value = false;
  axios
    .get("/api/getnewrtcid")
    .then((response) => {
      rtcId.value = response.data;
      canGetRtcId.value = true;
    })
    .catch((error) => {
      console.log(error);
      error.value = "Error generating RTC ID" + error.toString();
      canGetRtcId.value = false;
    });
}

const moduleAttributes = ref({});

onMounted(async () => {
  if (route.query.queue == "1") {
    rtcId.value = route.query.rtcId;
    inQueue.value = true;
    dialed.value = true;
    dial();
  } else {
    generateRtcId();
  }

  name.value = (await window.localStorageCustom.getItem("fullname")) || "";
  // get demopage hook modules from ModuleManager
  var self = this;
  ModuleManager.getHookModules("DemoPageMounted").forEach((module) => {
    module.run({
      addAttribute: (attr) => {
        moduleAttributes.value[attr.key] = attr;
        moduleAttributes.value[attr.key].value = attr.defaultValue || "";
        console.log("moduleAttributes added", moduleAttributes.value[attr.key]);
      },
      vueInstance: self,
    });
  });
});

async function dial() {
  canPlaceCall.value = false;
  try {
    let apiUrl = window.CONFIG?.API_URL ? window.CONFIG.API_URL : window.location.origin;
    let socketProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
    let host = new URL(apiUrl).host;

    let queueSocketUrl = socketProtocol + "://" + host + "/queue-socket/" + rtcId.value;

    let queueSocket = new WebSocket(queueSocketUrl);
    // wait for socket to open
    await new Promise((resolve) => {
      queueSocket.onopen = function () {
        resolve();
      };
    });

    queueSocket.onmessage = function (event) {
      console.log(event);
      let queueMessage = JSON.parse(event.data);
      if (queueMessage.messageType == "AgentConnected") {
        queueSocket.close();

        var currentUrl = new URL(window.location.href);
        var newUrl = new URL("/r/" + rtcId.value, window.location.origin);
        currentUrl.searchParams.forEach((value, key) => {
          newUrl.searchParams.set(key, value);
        });

        newUrl.searchParams.delete("rtcId");
        newUrl.searchParams.delete("queue");

        window.location.href = newUrl.href;
      }

      // CallDisconnected and SocketTimeout should reset the state add error message
      if (queueMessage.messageType == "CallDisconnected" || queueMessage.messageType == "SocketTimeout") {
        error.value = queueMessage.message;
        inQueue.value = false;
        queueSocket.close();
        generateRtcId();
      }
    };

    // on close reconnect to queue socket

    queueSocket.onclose = function () {
      console.log("Queue socket closed");
      if (inQueue.value) {
        console.log("inQueue is true Reconnecting to queue socket");
        queueSocket = new WebSocket(queueSocketUrl);
      }
    };

    if (!dialed.value) {
      error.value = "";
      await window.localStorageCustom.setItem("fullname", name.value);

      let dialRequest = {
        RtcId: rtcId.value,
        CallerName: name.value,
        Attributes: {},
      };

      // add moduleAttributes to dialRequest.Attributes
      for (const key in moduleAttributes.value) {
        dialRequest.Attributes[key] = moduleAttributes.value[key].value;
      }

      let dialResp = await axios.post("/api/dialex", dialRequest);

      if (!dialResp?.data?.InteractionId) {
        // Error
        error.value = dialResp.data.Message;
        return;
      }

      inQueue.value = true;

      // set url rtcId to query param
      var currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("rtcId", rtcId.value);
      currentUrl.searchParams.set("queue", "1");
      window.history.replaceState({}, "", currentUrl.href);

      if (configurationProvider.getBool("CALL_ANNOUNCEMENTS_ENABLED", true)) {
        const announcements = dialResp?.data?.announcements;
        if (announcements && announcements?.length > 0) {
          for (const announcement of announcements) {
            const announcementText =
              announcement?.translations?.find((t) => t.languageCode === i18n?.global?.locale)?.text ??
              announcement?.translations?.find((t) => t?.languageCode === i18n?.global?.fallbackLocale)?.text ??
              undefined;
            if (!announcementText) continue;

            try {
              announcement.style = JSON.parse(announcement.style);
            } catch (err) {
              console.error(err);
              announcement.style = {};
            }

            try {
              announcement.settings = JSON.parse(announcement.settings);
            } catch (err) {
              console.error(err);
              announcement.settings = {};
            }

            marqueeAnnouncements.value.push({
              text: "🕒 " + announcementText,
              duration: announcement?.settings?.duration,
              delayBeforeStart: Number(announcement?.settings?.delayBeforeStart ?? 0) * 1000,
              delayCycleBeforeStart: Number(announcement?.settings?.delayCycleBeforeStart ?? 0) * 1000,
              pauseOnHover: announcement?.settings?.pauseOnHover,
              repeat: announcement?.settings?.limitCyclePerCall,
              reverse: announcement?.settings?.reverse,
              style: {
                color: announcement?.style?.color,
                backgroundColor: announcement?.style?.backgroundColor,
                height: announcement?.style?.height,
                fontFamily: announcement?.style?.fontFamily,
                fontSize: announcement?.style?.fontSize,
                fontWeight: announcement?.style?.fontWeight,
                position: announcement?.style?.position == "bottom" ? "fixed" : "static",
                [announcement?.style?.position ?? "top"]: "0px",
              },
            });
          }

          if (marqueeAnnouncements.value.length > 0) marqueeDisabled.value = false;
        }
      }
    }
  } catch (err) {
    console.error(err);
    error.value = err.toString();
    inQueue.value = false;
  }

  canPlaceCall.value = true;
}

const buttonEnabled = computed(() => {
  return canGetRtcId.value && deviceState.canUseCamera && canPlaceCall.value;
});
</script>

<template>
  <VueMarquee
    v-if="!marqueeDisabled"
    :disabled="marqueeDisabled"
    :texts="marqueeAnnouncements"
    @onStatusChange="
      (status) => {
        if (status == 'stopped') {
          marqueeDisabled = true;
        }
        console.log({ marqueeStatus: status });
      }
    " />
  <v-sheet style="height: 100%" class="bg-deep-purple pa-12" rounded>
    <v-card class="mx-auto px-6 py-8" max-width="500">
      <v-card-title class="text-h5">{{ i18n.global.t("DemoTitle") }}</v-card-title>
      <v-card-text v-if="!inQueue">
        <v-form validate-on="submit" @submit.prevent="dial">
          <v-row>
            <v-alert :text="error" type="error" v-if="error"></v-alert>
            <v-col cols="12">
              <v-field append-inner-icon="mdi-refresh" @click:append-inner="generateRtcId" :label="rtcId"></v-field>
            </v-col>
            <v-col cols="12">
              <change-devices></change-devices>
            </v-col>
            <v-col cols="12">
              <v-text-field :label="i18n.global.t('NameSurname')" v-model="name"></v-text-field>
              <!-- loop through moduleAttributes and create input fields -->
              <v-text-field
                v-for="(attribute, key) in moduleAttributes"
                :key="key"
                :label="attribute.label"
                v-model="attribute.value"></v-text-field>
            </v-col>

            <v-col cols="12" v-for="error in deviceState.errors" :key="error">
              <v-alert :text="error" type="error"></v-alert>
            </v-col>
            <v-col>
              <v-btn :disabled="!buttonEnabled" type="submit" color="green" block>
                {{ i18n.global.t("Dial") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-text v-else>
        <v-row>
          <v-col cols="12">
            <v-alert icon="mdi-information-outline" :text="i18n.global.t('DemoInQueue')" type="info"></v-alert>
          </v-col>
          <v-col style="display: flex; justify-content: center">
            <v-progress-circular size="100" indeterminate="true"></v-progress-circular>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-sheet>
</template>
